import { CUSTOM_DISCLAIMER_FIELD_LIMITS } from '../constants/customDisclaimers';
import type {
  CustomDisclaimer,
  DisclaimerNotice,
  DisclaimerDraft,
  DisclaimerFieldErrors,
} from '../types/customDisclaimers';

export const createEmptyDisclaimerDraft = (): DisclaimerDraft => ({
  name: '',
  organisationName: '',
  text: '',
  policyIds: [],
});

export const normalizeDisclaimerDraft = (draft: DisclaimerDraft): DisclaimerDraft => ({
  name: draft.name.trim(),
  organisationName: draft.organisationName.trim(),
  text: draft.text.trim(),
  policyIds: Array.from(new Set(draft.policyIds)),
});

export const validateDisclaimerDraft = (draft: DisclaimerDraft): DisclaimerFieldErrors => {
  const errors: DisclaimerFieldErrors = {};

  if (!draft.name.trim()) {
    errors.name = 'Enter a disclaimer name.';
  } else if (draft.name.length > CUSTOM_DISCLAIMER_FIELD_LIMITS.name) {
    errors.name = `Disclaimer name must be ${CUSTOM_DISCLAIMER_FIELD_LIMITS.name} characters or fewer.`;
  }

  if (!draft.organisationName.trim()) {
    errors.organisationName = 'Enter an organisation name.';
  } else if (draft.organisationName.length > CUSTOM_DISCLAIMER_FIELD_LIMITS.organisationName) {
    errors.organisationName = `Organisation name must be ${CUSTOM_DISCLAIMER_FIELD_LIMITS.organisationName} characters or fewer.`;
  }

  if (!draft.text.trim()) {
    errors.text = 'Enter disclaimer text.';
  } else if (draft.text.length > CUSTOM_DISCLAIMER_FIELD_LIMITS.text) {
    errors.text = `Disclaimer text must be ${CUSTOM_DISCLAIMER_FIELD_LIMITS.text} characters or fewer.`;
  }

  return errors;
};

export const hasDisclaimerFieldErrors = (errors: DisclaimerFieldErrors): boolean =>
  Object.keys(errors).length > 0;

export const toDisclaimerDraft = (disclaimer: CustomDisclaimer): DisclaimerDraft => ({
  name: disclaimer.name,
  organisationName: disclaimer.organisationName,
  text: disclaimer.text,
  policyIds: [...disclaimer.policyIds],
});

export const disclaimerDraftsMatch = (left: DisclaimerDraft, right: DisclaimerDraft): boolean => {
  const normalizedLeft = normalizeDisclaimerDraft(left);
  const normalizedRight = normalizeDisclaimerDraft(right);
  const comparePolicyIds = (leftPolicyId: string, rightPolicyId: string) =>
    leftPolicyId.localeCompare(rightPolicyId);
  const sortedLeftPolicyIds = [...normalizedLeft.policyIds].sort(comparePolicyIds);
  const sortedRightPolicyIds = [...normalizedRight.policyIds].sort(comparePolicyIds);

  return normalizedLeft.name === normalizedRight.name
    && normalizedLeft.organisationName === normalizedRight.organisationName
    && normalizedLeft.text === normalizedRight.text
    && sortedLeftPolicyIds.length === sortedRightPolicyIds.length
    && sortedLeftPolicyIds.every((policyId, index) => policyId === sortedRightPolicyIds[index]);
};

export const formatDisclaimerUpdatedAt = (value: string, includeTime = false): string => {
  const options: Intl.DateTimeFormatOptions = includeTime
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' };

  return new Intl.DateTimeFormat('en-GB', options).format(new Date(value));
};

export const formatDisclaimerPolicySummary = (disclaimer: CustomDisclaimer): string => {
  if (disclaimer.policies.length === 0) {
    return 'Unassigned';
  }

  const visiblePolicies = disclaimer.policies.slice(0, 2).map((policy) => policy.name);
  const remainingCount = disclaimer.policies.length - visiblePolicies.length;
  const visiblePolicySummary = visiblePolicies.join(', ');
  if (remainingCount === 0) {
    return visiblePolicySummary;
  }

  const remainingPolicyLabel = remainingCount === 1 ? 'policy' : 'policies';
  return `${visiblePolicySummary}, +${remainingCount} ${remainingPolicyLabel}`;
};

export const getDisclaimerPolicyInitials = (policyName: string): string => {
  const initials = policyName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  return initials || 'P';
};

export const createDisclaimerCreatedNotice = (
  disclaimerName: string,
  assignedPolicyCount: number,
): DisclaimerNotice => {
  const assignedPolicyLabel = assignedPolicyCount === 1 ? 'policy' : 'policies';
  const message = assignedPolicyCount === 0
    ? 'was created. Assign it to a policy for it to appear in Microsoft Teams.'
    : `was created and assigned to ${assignedPolicyCount} ${assignedPolicyLabel}. Users must complete enrollment before the disclaimer appears in Microsoft Teams.`;

  return {
    intent: 'success',
    disclaimerName,
    message,
  };
};

export const createDisclaimerDeletedNotice = (
  disclaimerName: string,
): DisclaimerNotice => ({
  intent: 'success',
  disclaimerName,
  message: 'was deleted.',
});

export const createDisclaimerUpdatedNotice = (
  disclaimerName: string,
): DisclaimerNotice => ({
  intent: 'success',
  disclaimerName,
  message: 'was updated.',
});

export const formatDisclaimerPreviewTitle = (organisationName: string): string =>
  `${organisationName.trim().toUpperCase()} CHATROOM DISCLAIMER`;

export const getEnrollmentLinkPreview = (value: string): string => {
  try {
    return `${new URL(value).origin}/...`;
  } catch {
    return 'Enrollment link ready';
  }
};